/**
 * Validation for post-login redirect targets (AB#4528).
 *
 * The app captures the URL a user arrived on so it can send them back there after
 * authenticating. That value is built from `location.pathname + search + hash`, is
 * parked in sessionStorage, round-trips through the auth provider as the OAuth
 * `state` parameter, and finally reaches `navigate()`. Every one of those hops is
 * attacker-influencable by handing someone a crafted link, so the value is treated
 * as untrusted input and normalised to a same-origin path before it is stored or
 * used.
 *
 * Anything that is not plainly an internal path collapses to '/'. That includes
 * protocol-relative forms ('//evil.com'), the backslash variants browsers
 * normalise to them, absolute URLs, scheme-like values ('javascript:...'), and
 * embedded control characters such as tab or newline, which URL parsers strip and
 * which can therefore smuggle a '//' past a naive prefix check.
 *
 * React Router's `navigate()` does not evaluate schemes, so this is not guarding
 * an XSS sink. It guards a redirect: `navigate(to)` without `replace` goes through
 * history `push`, which falls back to `window.location.assign(url)` when
 * `pushState` throws on a cross-origin URL -- a real open redirect. The current
 * call site passes `replace: true`, which takes the `replaceState` path and has no
 * such fallback, so dropping that one option would be enough to open the hole.
 * Validating here means the call site's options no longer carry that weight.
 */
// Matching control characters is the point here, so this is an explicit scan rather
// than a regex: eslint's no-control-regex exists to catch them by accident.
const hasControlCharacter = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 0x1f || code === 0x7f) {
      return true
    }
  }

  return false
}

const PROTOCOL_RELATIVE = /^\/[/\\]/

export const SAFE_REDIRECT_FALLBACK = '/'

export const safeInternalPath = (candidate: string | null | undefined): string => {
  if (!candidate) {
    return SAFE_REDIRECT_FALLBACK
  }

  if (!candidate.startsWith('/')) {
    return SAFE_REDIRECT_FALLBACK
  }

  if (PROTOCOL_RELATIVE.test(candidate) || hasControlCharacter(candidate)) {
    return SAFE_REDIRECT_FALLBACK
  }

  return candidate
}
